import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import * as productService from '../../services/productService';
import styles from './CategoriesPage.module.css';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Card 53600: hoisted so the failure branch can RE-ISSUE this request from a Try again
  // control. `getCategories` is a LIST route with no legitimate 404 — productController.js:191
  // states a missing/unknown list answers 200 with an empty array, never 404 — so EVERY error
  // here is a load failure and none of them may render as "No categories available".
  const loadCategories = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await productService.getCategories();
      setCategories(response.categories || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  if (loading) {
    return (
      <div className={styles.page}>
        <LoadingSpinner fullScreen />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>Treatment Categories</h1>
          <p>Explore our range of FDA-approved telehealth treatments</p>
        </div>

        <div className={styles.grid}>
          {categories.map((category) => (
            <Link key={category.id} to={`/categories/${category.slug}`}>
              <Card hoverable className={styles.card}>
                <div 
                  className={styles.cardBg}
                  style={{ backgroundColor: category.color || 'var(--color-primary)' }}
                />
                <div className={styles.cardContent}>
                  <div className={styles.icon}>
                    {category.icon || '💊'}
                  </div>
                  <h2>{category.name}</h2>
                  <p>{category.description || category.shortDescription}</p>
                  <span className={styles.productCount}>
                    {category.productCount || 0} Products
                  </span>
                  <span className={styles.arrow}>→</span>
                </div>
              </Card>
            </Link>
          ))}
        </div>

        {loadError ? (
          <div className={styles.empty} role="alert">
            <p>We could not load the treatment categories.</p>
            <Button onClick={loadCategories}>Try again</Button>
          </div>
        ) : categories.length === 0 ? (
          <div className={styles.empty}>
            <p>No categories available</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CategoriesPage;

